import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Sparkles, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight, ChevronRight, RefreshCw, AlertTriangle, Zap, Target, Users2, CalendarCheck, DollarSign, Rocket, HeartPulse, Percent, Radio, Mail, Award, Activity } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, PieChart, Pie, Cell, AreaChart, Area } from "recharts";
import { useState } from "react";
import { TID } from "@/constants/testIds";

const COLORS = ["#0c4ad9", "#38bdf8", "#a855f7", "#10b981", "#f59e0b", "#f43f5e", "#64748b", "#0ea5e9", "#8b5cf6"];

export default function DashboardPage() {
    const kpi = useQuery({ queryKey: ["kpi"], queryFn: () => api.get("/kpi/summary").then((r) => r.data) });
    const [range, setRange] = useState("30d");

    return (
        <div className="space-y-10 pv-enter">
            <Header range={range} setRange={setRange} />
            <StrategicPanel />
            <KPIGrid data={kpi.data} loading={kpi.isLoading} />
            <ChartsRow data={kpi.data} />
            <SecondaryRow data={kpi.data} />
        </div>
    );
}

function Header({ range, setRange }) {
    const ranges = ["oggi", "7d", "30d", "90d", "12m"];
    return (
        <div className="flex items-end justify-between">
            <div>
                <div className="pv-eyebrow mb-2">Command Center</div>
                <h1 className="pv-serif text-5xl tracking-tight leading-none">Buongiorno.</h1>
                <p className="mt-3 text-[14px] text-textmuted max-w-xl">
                    Tutto ciò che serve, in un solo posto. Prime Vision AI ha già letto i dati di stanotte.
                </p>
            </div>
            <div className="hidden md:flex items-center gap-1 rounded-xl border border-border/[var(--border-opacity)] bg-surface p-1">
                {ranges.map((r) => (
                    <button
                        key={r}
                        onClick={() => setRange(r)}
                        className={`text-[12px] px-3 py-1.5 rounded-lg transition-colors ${
                            range === r ? "bg-primary text-primary-foreground" : "text-textmuted hover:text-textmain"
                        }`}
                    >{r}</button>
                ))}
            </div>
        </div>
    );
}

function StrategicPanel() {
    const q = useQuery({ queryKey: ["strategic"], queryFn: () => api.get("/ai/strategic-daily").then((r) => r.data) });
    const [refreshing, setRefreshing] = useState(false);
    const d = q.data;

    const refresh = async () => {
        setRefreshing(true);
        try {
            await api.get("/ai/strategic-daily?force=true");
            await q.refetch();
        } finally {
            setRefreshing(false);
        }
    };

    return (
        <div data-testid={TID.strategicPanel} className="relative pv-card overflow-hidden">
            <div className="absolute -top-20 -right-20 h-64 w-64 rounded-full bg-primary/[0.08] blur-3xl" />
            <div className="absolute -bottom-16 -left-16 h-56 w-56 rounded-full bg-azure/[0.10] blur-3xl" />
            <div className="relative p-8">
                <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-2.5">
                        <div className="h-8 w-8 rounded-xl bg-primary/10 flex items-center justify-center">
                            <Sparkles size={15} strokeWidth={1.5} className="text-primary" />
                        </div>
                        <div>
                            <div className="text-[10.5px] uppercase tracking-[0.18em] text-textsubtle">Prime Vision AI</div>
                            <div className="text-[15px] font-medium">Analisi strategica di oggi</div>
                        </div>
                    </div>
                    <button
                        onClick={refresh}
                        data-testid={TID.strategicRefresh}
                        disabled={refreshing}
                        className="flex items-center gap-1.5 text-[12px] text-textmuted hover:text-textmain px-2.5 py-1.5 rounded-lg hover:bg-surface2 disabled:opacity-50 transition-colors"
                    >
                        <RefreshCw size={12} strokeWidth={1.5} className={refreshing ? "animate-spin" : ""} /> Rigenera
                    </button>
                </div>

                {q.isLoading || !d ? (
                    <div className="text-[14px] text-textmuted">Prime Vision AI sta leggendo i dati…</div>
                ) : (
                    <>
                        <div className="pv-serif text-3xl leading-tight tracking-tight max-w-3xl">{d.headline}</div>

                        <div className="grid md:grid-cols-2 gap-6 mt-8">
                            <StratColumn title="Cosa sta andando bene" items={d.going_well} tone="success" />
                            <StratColumn title="Cosa non sta andando" items={d.not_going_well} tone="critical" />
                            <StratColumn title="Possibili cause" items={d.root_causes} tone="muted" />
                            <div>
                                <div className="pv-eyebrow mb-3 flex items-center gap-2"><Zap size={11} strokeWidth={1.5} />Azioni consigliate</div>
                                <div className="space-y-2">
                                    {d.actions?.map((a, i) => (
                                        <div key={i} className="flex items-start gap-3 pv-hover pv-card px-3 py-2.5">
                                            <div className="h-6 w-6 rounded-lg bg-primary/[0.08] text-primary text-[11px] font-medium flex items-center justify-center flex-shrink-0 mt-0.5">
                                                {i + 1}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-[13.5px] font-medium">{a.title}</div>
                                                <div className="text-[11px] text-textsubtle mt-0.5">{a.owner} · impatto {a.impact}</div>
                                            </div>
                                            <ChevronRight size={14} strokeWidth={1.5} className="text-textsubtle mt-1" />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="mt-6 pt-5 border-t border-border/[var(--border-opacity)] flex items-center justify-between">
                            <div className="text-[13px] text-textmain max-w-2xl">
                                <span className="pv-eyebrow mr-2">Risultato stimato</span>
                                {d.estimated_result}
                            </div>
                            <div className="text-[11px] text-textsubtle">Confidenza {d.confidence ?? 75}%</div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

function StratColumn({ title, items, tone }) {
    const dot = tone === "success" ? "bg-success" : tone === "critical" ? "bg-critical" : "bg-textsubtle";
    return (
        <div>
            <div className="pv-eyebrow mb-3">{title}</div>
            <ul className="space-y-2">
                {items?.map((it, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-[13px] leading-snug">
                        <span className={`mt-1.5 h-1.5 w-1.5 rounded-full ${dot} flex-shrink-0`} />
                        <span>{it}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

const KPI_DEFS = (d) => [
    { key: "leads", label: "Lead", value: d?.leads?.total ?? 0, sub: `+${d?.leads?.new_today ?? 0} oggi`, icon: Users2, trend: d?.leads?.trend, tone: "success" },
    { key: "demos-done", label: "Demo effettuate", value: d?.demos?.done ?? 0, sub: `${d?.demos?.booked ?? 0} prenotate`, icon: CalendarCheck, trend: d?.demos?.trend, tone: "success" },
    { key: "active-clients", label: "Clienti attivi", value: d?.clients?.active ?? 0, sub: `${d?.clients?.inactive ?? 0} persi`, icon: Award, trend: d?.clients?.trend, tone: "success" },
    { key: "mrr", label: "MRR", value: `€${(d?.revenue?.mrr ?? 0).toLocaleString("it-IT")}`, sub: `ARR €${(d?.revenue?.arr ?? 0).toLocaleString("it-IT")}`, icon: DollarSign, trend: d?.revenue?.trend, tone: "success" },
    { key: "conv-lead-demo", label: "Lead → Demo", value: `${d?.conversions?.lead_to_demo ?? 0}%`, sub: "conversione", icon: Target, tone: "success" },
    { key: "conv-demo-client", label: "Demo → Cliente", value: `${d?.conversions?.demo_to_client ?? 0}%`, sub: "conversione", icon: Percent, tone: (d?.conversions?.demo_to_client ?? 0) < 25 ? "critical" : "success" },
    { key: "webinars", label: "Webinar svolti", value: d?.webinars?.count ?? 0, sub: `${d?.webinars?.participants ?? 0} partecipanti`, icon: Radio, trend: d?.webinars?.trend, tone: "success" },
    { key: "nl-open", label: "Apertura newsletter", value: `${d?.newsletter?.open_rate ?? 0}%`, sub: `CTR ${d?.newsletter?.ctr ?? 0}%`, icon: Mail, tone: "success" },
    { key: "nps", label: "NPS", value: d?.customer?.nps ?? 0, sub: `CSAT ${d?.customer?.csat ?? 0}/5`, icon: HeartPulse, tone: "success" },
    { key: "health", label: "Health Score medio", value: `${d?.customer?.avg_health_score ?? 0}`, sub: "clienti software", icon: Activity, tone: "success" },
    { key: "tickets", label: "Ticket aperti", value: d?.customer?.open_tickets ?? 0, sub: `${d?.customer?.closed_tickets ?? 0} chiusi`, icon: AlertTriangle, tone: (d?.customer?.open_tickets ?? 0) > 5 ? "warning" : "success" },
    { key: "logins", label: "Login 7 giorni", value: d?.usage?.logins_7d ?? 0, sub: `${d?.usage?.logins_30d ?? 0} in 30gg`, icon: Rocket, tone: "success" },
];

function KPIGrid({ data, loading }) {
    const items = KPI_DEFS(data);
    return (
        <section>
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-[15px] font-medium tracking-tight">KPI principali</h2>
                <div className="text-[11px] text-textsubtle">40+ metriche · aggiornate in tempo reale</div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 pv-stagger">
                {items.map((k) => <KPICard key={k.key} {...k} loading={loading} />)}
            </div>
        </section>
    );
}

function KPICard({ label, value, sub, icon: Icon, trend, tone, loading }) {
    const trendDelta = trend?.length >= 2 ? (trend.at(-1) - trend[0]) : 0;
    const up = trendDelta >= 0;
    const slug = label.toLowerCase().replace(/[^a-z0-9]+/g, "-");
    return (
        <div data-testid={TID.kpiCard(slug)}
             className="pv-card pv-hover p-5 relative overflow-hidden">
            <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                    <div className="text-[11px] uppercase tracking-[0.14em] text-textsubtle">{label}</div>
                    <div className="mt-2 text-[26px] font-medium tracking-tight leading-none">{loading ? "—" : value}</div>
                    <div className="mt-1.5 text-[11.5px] text-textmuted">{sub}</div>
                </div>
                <div className={`h-8 w-8 rounded-xl flex items-center justify-center ${
                    tone === "critical" ? "bg-critical/10 text-critical" :
                    tone === "warning" ? "bg-warning/10 text-warning" :
                    "bg-primary/[0.08] text-primary"
                }`}>
                    <Icon size={14.5} strokeWidth={1.5} />
                </div>
            </div>
            {trend?.length ? (
                <div className="mt-3 h-10 -mx-1">
                    <ResponsiveContainer>
                        <AreaChart data={trend.map((v, i) => ({ i, v }))}>
                            <defs>
                                <linearGradient id={`kpi-g-${slug}`} x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={tone === "critical" ? "#ef4444" : "#0c4ad9"} stopOpacity="0.28" />
                                    <stop offset="100%" stopColor={tone === "critical" ? "#ef4444" : "#0c4ad9"} stopOpacity="0" />
                                </linearGradient>
                            </defs>
                            <Area type="monotone" dataKey="v" stroke={tone === "critical" ? "#ef4444" : "#0c4ad9"} strokeWidth={1.5} fill={`url(#kpi-g-${slug})`} isAnimationActive={false} />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            ) : null}
            {trend?.length ? (
                <div className={`absolute top-3 right-14 text-[10.5px] pv-mono flex items-center gap-0.5 ${up ? "text-success" : "text-critical"}`}>
                    {up ? <ArrowUpRight size={11} strokeWidth={1.5} /> : <ArrowDownRight size={11} strokeWidth={1.5} />}
                    {Math.abs(Math.round(trendDelta))}
                </div>
            ) : null}
        </div>
    );
}

function ChartsRow({ data }) {
    const sources = Object.entries(data?.lead_sources || {}).map(([name, value]) => ({ name, value }));
    const trend = (data?.leads?.trend || []).map((v, i) => ({ i, Lead: v, Demo: (data?.demos?.trend?.[i] ?? 0), Clienti: (data?.clients?.trend?.[i] ?? 0) }));
    return (
        <section className="grid lg:grid-cols-3 gap-4">
            <div className="pv-card p-6 lg:col-span-2 pv-hover">
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <div className="text-[15px] font-medium">Andamento a 30 giorni</div>
                        <div className="text-[11.5px] text-textmuted">Lead · Demo · Clienti</div>
                    </div>
                    <div className="flex items-center gap-3 text-[11px] text-textmuted">
                        <Legend color="#0c4ad9" label="Lead" />
                        <Legend color="#38bdf8" label="Demo" />
                        <Legend color="#a855f7" label="Clienti" />
                    </div>
                </div>
                <div className="h-64">
                    <ResponsiveContainer>
                        <LineChart data={trend} margin={{ left: 6, right: 6, top: 8, bottom: 0 }}>
                            <XAxis dataKey="i" tick={{ fill: "rgb(148 163 184)", fontSize: 10 }} axisLine={false} tickLine={false} />
                            <YAxis hide />
                            <Tooltip />
                            <Line type="monotone" dataKey="Lead" stroke="#0c4ad9" strokeWidth={2} dot={false} isAnimationActive />
                            <Line type="monotone" dataKey="Demo" stroke="#38bdf8" strokeWidth={2} dot={false} isAnimationActive />
                            <Line type="monotone" dataKey="Clienti" stroke="#a855f7" strokeWidth={2} dot={false} isAnimationActive />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </div>

            <div className="pv-card p-6 pv-hover">
                <div className="text-[15px] font-medium mb-1">Sorgenti Lead</div>
                <div className="text-[11.5px] text-textmuted mb-4">Da dove arrivano i contatti attivi</div>
                <div className="h-56">
                    <ResponsiveContainer>
                        <PieChart>
                            <Pie data={sources} innerRadius={48} outerRadius={78} paddingAngle={3} dataKey="value" nameKey="name" isAnimationActive>
                                {sources.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                            </Pie>
                            <Tooltip />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[11.5px]">
                    {sources.slice(0, 8).map((s, i) => (
                        <div key={s.name} className="flex items-center gap-1.5 min-w-0">
                            <span className="h-2 w-2 rounded-full flex-shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
                            <span className="truncate text-textmuted">{s.name}</span>
                            <span className="pv-mono ml-auto">{s.value}</span>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function Legend({ color, label }) {
    return (
        <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full" style={{ background: color }} />
            {label}
        </span>
    );
}

function SecondaryRow({ data }) {
    const usage = [
        { name: "Oggi", v: data?.usage?.logins_today ?? 0 },
        { name: "7 giorni", v: data?.usage?.logins_7d ?? 0 },
        { name: "30 giorni", v: data?.usage?.logins_30d ?? 0 },
    ];
    return (
        <section className="grid lg:grid-cols-3 gap-4">
            <div className="pv-card p-6 pv-hover">
                <div className="text-[15px] font-medium mb-1">Utilizzo software</div>
                <div className="text-[11.5px] text-textmuted mb-4">Login attivi per finestra temporale</div>
                <div className="h-40">
                    <ResponsiveContainer>
                        <BarChart data={usage}>
                            <XAxis dataKey="name" tick={{ fill: "rgb(148 163 184)", fontSize: 10 }} axisLine={false} tickLine={false} />
                            <YAxis hide />
                            <Tooltip />
                            <Bar dataKey="v" radius={[8, 8, 0, 0]} fill="#0c4ad9" isAnimationActive />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
            <div className="pv-card p-6 pv-hover">
                <div className="text-[15px] font-medium mb-1">Newsletter</div>
                <div className="text-[11.5px] text-textmuted mb-4">Sanità del canale email</div>
                <MetricRow label="Inviate" value={data?.newsletter?.sent ?? 0} />
                <MetricRow label="Apertura media" value={`${data?.newsletter?.open_rate ?? 0}%`} />
                <MetricRow label="CTR medio" value={`${data?.newsletter?.ctr ?? 0}%`} />
                <MetricRow label="Disiscrizioni" value={data?.newsletter?.unsubs ?? 0} />
            </div>
            <div className="pv-card p-6 pv-hover">
                <div className="text-[15px] font-medium mb-1">Customer Success</div>
                <div className="text-[11.5px] text-textmuted mb-4">Salute della base clienti</div>
                <MetricRow label="Health Score medio" value={data?.customer?.avg_health_score ?? 0} />
                <MetricRow label="NPS" value={data?.customer?.nps ?? 0} />
                <MetricRow label="CSAT" value={`${data?.customer?.csat ?? 0}/5`} />
                <MetricRow label="Feedback raccolti" value={data?.customer?.feedback_count ?? 0} />
            </div>
        </section>
    );
}

function MetricRow({ label, value }) {
    return (
        <div className="flex items-center justify-between py-1.5 border-b border-border/[var(--border-opacity)] last:border-0">
            <span className="text-[12.5px] text-textmuted">{label}</span>
            <span className="text-[14px] font-medium pv-mono">{value}</span>
        </div>
    );
}
